import { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Globe, Sparkles, Plus, RefreshCw, AlertCircle, Check, Trash2, ExternalLink,
  TrendingUp, Home as HomeIcon, Fingerprint, Calendar as CalIcon, FileText,
  Share2, BarChart3, Search as SearchIcon, Gauge, X, Facebook, Instagram,
  Image as ImageIcon, Wand2, Copy, Download,
} from "lucide-react";
import { toast } from "sonner";
import ImagePicker from "@/components/ImagePicker";

const PRIORITY_STYLES = {
  alta: "border-[#2D6A4F] text-[#2D6A4F] bg-[#2D6A4F]/5",
  media: "border-[#D95D39] text-[#D95D39] bg-[#D95D39]/5",
  baja: "border-[#8A8F98] text-[#8A8F98]",
};
const STATUS_STYLES = {
  draft: "border-[#8A8F98] text-[#8A8F98]",
  scheduled: "border-[#D95D39] text-[#D95D39] bg-[#D95D39]/5",
  published: "border-[#2D6A4F] text-[#2D6A4F] bg-[#2D6A4F]/5",
  failed: "border-[#9D0208] text-[#9D0208] bg-[#9D0208]/5",
};

const SUBNAV = [
  { key: "home", label: "Inicio", icon: HomeIcon },
  { key: "brand", label: "Brand DNA", icon: Fingerprint },
  { key: "calendar", label: "Calendario", icon: CalIcon },
  { key: "articles", label: "Artículos", icon: FileText },
  { key: "social", label: "Redes sociales", icon: Share2 },
  { key: "analytics", label: "Analytics", icon: BarChart3 },
  { key: "keywords", label: "Keywords", icon: SearchIcon },
  { key: "speed", label: "Site Speed", icon: Gauge },
];

const IMAGE_STYLES = [
  { id: "clean", label: "Clean & Minimal", desc: "Ilustraciones planas profesionales" },
  { id: "photorealistic", label: "Photorealistic", desc: "Estilo foto stock" },
  { id: "watercolor", label: "Watercolor", desc: "Estética acuarela artística" },
  { id: "3d", label: "3D Render", desc: "Ilustraciones 3D modernas" },
  { id: "vintage", label: "Vintage", desc: "Estilo retro clásico" },
  { id: "abstract", label: "Abstract", desc: "Formas y gradientes audaces" },
];

export default function SeoPage() {
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [section, setSection] = useState("brand");

  const [keywords, setKeywords] = useState([]);
  const [articles, setArticles] = useState([]);

  const [addOpen, setAddOpen] = useState(false);
  const [siteForm, setSiteForm] = useState(emptySiteForm());

  const [seedsForm, setSeedsForm] = useState({ seeds: "", audience: "" });
  const [genLoading, setGenLoading] = useState(false);

  const [editArt, setEditArt] = useState(null);
  const [scheduleArt, setScheduleArt] = useState(null);
  const [scheduleDate, setScheduleDate] = useState("");

  const [planCount, setPlanCount] = useState(4);
  const [planLoading, setPlanLoading] = useState(false);

  const [socialArt, setSocialArt] = useState(null);
  const [socialImageUrl, setSocialImageUrl] = useState("");
  const [socialHeadline, setSocialHeadline] = useState("");
  const [socialLoading, setSocialLoading] = useState(false);
  const [socialResult, setSocialResult] = useState(null);

  const [regenImageId, setRegenImageId] = useState(null);
  const [genSocialId, setGenSocialId] = useState(null);

  const generateSocialCopy = async (art) => {
    setGenSocialId(art.id);
    toast.info("Generando copy de FB + IG con IA...");
    try {
      await api.post(`/seo/articles/${art.id}/regenerate-social`);
      toast.success("Copy de RRSS generado");
      loadAll(siteId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando copy");
    } finally {
      setGenSocialId(null);
    }
  };

  const regenerateImage = async (art) => {
    setRegenImageId(art.id);
    toast.info("Generando imagen con Gemini Nano Banana...");
    try {
      const style = currentSite?.image_style || "photorealistic";
      const r = await api.post(`/seo/articles/${art.id}/regenerate-image`, {
        style,
        custom_instructions: currentSite?.custom_image_instructions || null,
      });
      if (r.data?.hero_image) {
        toast.success("Imagen generada");
        // If article was already in WP, re-publish to push the new image
        if (art.wp_post_id) {
          toast.info("Subiendo la imagen a WordPress...");
          try {
            await api.post(`/seo/articles/${art.id}/publish-wp`, { mode: art.status === "published" ? "publish" : "draft" });
            toast.success("Imagen subida a WordPress");
          } catch (e) {
            toast.warning("Imagen guardada localmente; falló la actualización en WP: " + (e.response?.data?.detail || ""));
          }
        }
        loadAll(siteId);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando imagen");
    } finally {
      setRegenImageId(null);
    }
  };

  function emptySiteForm() {
    return {
      url: "", wp_user: "", wp_app_password: "",
      content_language: "es",
      business_type: "", brand_voice: "", target_audience: "",
      service_area: [], topics_to_avoid: [], key_themes: [],
      business_goals: "", differentiators: "",
      create_images: true, custom_image_instructions: "", image_style: "photorealistic",
    };
  }

  const loadSites = () => api.get("/seo/sites").then((r) => {
    setSites(r.data);
    if (!siteId && r.data.length > 0) setSiteId(r.data[0].id);
  });

  const loadAll = (sid) => {
    if (!sid) return;
    api.get(`/seo/sites/${sid}/keywords`).then((r) => setKeywords(r.data));
    api.get(`/seo/articles?site_id=${sid}`).then((r) => setArticles(r.data));
  };

  useEffect(() => { loadSites(); }, []);
  useEffect(() => { loadAll(siteId); /* eslint-disable-next-line */ }, [siteId]);

  const currentSite = sites.find((s) => s.id === siteId);

  // ---- Site CRUD ----
  const addSite = async () => {
    if (!siteForm.url) return toast.error("URL requerida");
    try {
      await api.post("/seo/sites", siteForm);
      toast.success("Sitio añadido");
      setAddOpen(false);
      setSiteForm(emptySiteForm());
      loadSites();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };
  const testSite = async () => {
    if (!siteId) return;
    const r = await api.post(`/seo/sites/${siteId}/test`);
    if (r.data.ok) toast.success(`Conectado como ${r.data.name}`);
    else toast.error(`Fallo conexión: status ${r.data.status}`);
    loadSites();
  };
  const deleteSite = async () => {
    if (!siteId || !confirm("¿Eliminar este sitio y todos sus datos?")) return;
    await api.delete(`/seo/sites/${siteId}`);
    setSiteId("");
    loadSites();
  };

  // ---- Brand DNA edit ----
  const updateBrandField = (field, value) => {
    if (!currentSite) return;
    setSites(sites.map((s) => s.id === currentSite.id ? { ...s, [field]: value } : s));
  };
  const saveBrand = async () => {
    if (!currentSite) return;
    try {
      const payload = {
        url: currentSite.url,
        wp_user: currentSite.wp_user || "",
        content_language: currentSite.content_language || "es",
        business_type: currentSite.business_type || "",
        brand_voice: currentSite.brand_voice || "",
        target_audience: currentSite.target_audience || "",
        service_area: currentSite.service_area || [],
        topics_to_avoid: currentSite.topics_to_avoid || [],
        key_themes: currentSite.key_themes || [],
        business_goals: currentSite.business_goals || "",
        differentiators: currentSite.differentiators || "",
        create_images: currentSite.create_images !== false,
        custom_image_instructions: currentSite.custom_image_instructions || "",
        image_style: currentSite.image_style || "photorealistic",
      };
      await api.post("/seo/sites", payload);
      toast.success("Brand DNA guardado");
      loadSites();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  // ---- Keywords / articles actions ----
  const generatePlan = async () => {
    if (!siteId) return toast.error("Selecciona un sitio");
    setGenLoading(true);
    toast.info("Generando plan de keywords con IA + DataForSEO (puede tardar 1-2 min)...");
    try {
      const r = await api.post(`/seo/sites/${siteId}/keywords/generate`, {
        seeds: seedsForm.seeds.split(",").map((s) => s.trim()).filter(Boolean),
        audience: seedsForm.audience || null,
      }, { timeout: 180000 });
      toast.success(`${r.data.count} keywords generadas`);
      loadAll(siteId);
      setSection("keywords");
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Error generando keywords";
      toast.error(`Error: ${msg}`);
      console.error("Keyword plan error:", e);
    } finally { setGenLoading(false); }
  };
  const refreshPosition = async (k) => {
    toast.info(`Consultando posición de "${k.keyword}"...`);
    try {
      const r = await api.post(`/seo/sites/${siteId}/keywords/${k.id}/refresh-position`);
      toast.success(r.data.position ? `Posición actual: #${r.data.position}` : "No rankea en el top 100");
      loadAll(siteId);
    } catch { toast.error("Error consultando posición"); }
  };
  const genArticle = async (k) => {
    toast.info("Generando artículo con IA...");
    try {
      await api.post("/seo/articles/generate", { site_id: siteId, keyword_id: k.id, focus_keyword: k.keyword, length: "medium" });
      toast.success("Artículo borrador creado");
      loadAll(siteId);
      setSection("articles");
    } catch { toast.error("Error generando artículo"); }
  };
  const genPlanBatch = async () => {
    setPlanLoading(true);
    toast.info(`Generando ${planCount} artículos programados...`);
    try {
      const r = await api.post("/seo/plan/generate", { site_id: siteId, count: Number(planCount), start_offset_days: 1 });
      toast.success(`${r.data.count} artículos programados`);
      loadAll(siteId);
    } catch { toast.error("Error generando plan"); }
    finally { setPlanLoading(false); }
  };
  const saveEdit = async () => {
    if (!editArt) return;
    await api.patch(`/seo/articles/${editArt.id}`, editArt);
    toast.success("Artículo actualizado");
    setEditArt(null);
    loadAll(siteId);
  };
  const publishWP = async (art, mode, autoSocial = false) => {
    if (!currentSite?.wp_connected) return toast.error("Conecta WordPress primero");
    toast.info(`Enviando a WordPress como ${mode}...`);
    try {
      const r = await api.post(`/seo/articles/${art.id}/publish-wp`, { mode, auto_post_socials: autoSocial });
      const social = r.data?.social;
      if (autoSocial && social) {
        if (social.errors?.length) {
          toast.warning(`WP ok · RRSS con avisos: ${social.errors.join("; ")}`);
        } else {
          toast.success("Publicado en WP + Facebook + Instagram");
        }
      } else {
        toast.success(`Artículo ${mode === "publish" ? "publicado" : "guardado"} en WordPress`);
      }
      loadAll(siteId);
    } catch (e) { toast.error(e.response?.data?.detail || "Error publicando"); }
  };
  const autoPostSocial = async (art) => {
    if (!art.wp_link) return toast.error("Publica primero en WordPress");
    toast.info("Publicando en Facebook + Instagram...");
    try {
      const r = await api.post(`/seo/articles/${art.id}/post-social-auto`);
      if (r.data?.errors?.length) {
        toast.warning(`Publicado con avisos: ${r.data.errors.join("; ")}`);
      } else {
        toast.success("Publicado en Facebook + Instagram");
      }
      loadAll(siteId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error publicando en redes");
    }
  };
  const scheduleArticle = async () => {
    if (!scheduleArt || !scheduleDate) return;
    await api.patch(`/seo/articles/${scheduleArt.id}`, { scheduled_at: new Date(scheduleDate).toISOString() });
    toast.success("Programado");
    setScheduleArt(null);
    setScheduleDate("");
    loadAll(siteId);
  };
  const delArticle = async (id) => {
    if (!confirm("¿Eliminar este artículo?")) return;
    await api.delete(`/seo/articles/${id}`);
    loadAll(siteId);
  };

  // ---- Social posting ----
  const openSocialDialog = (art) => {
    setSocialArt(art);
    setSocialImageUrl("");
    setSocialHeadline("");
    setSocialResult(null);
  };

  const sendSocialPost = async () => {
    if (!socialArt) return;
    if (!socialImageUrl) return toast.error("Selecciona o pega una URL de imagen pública");
    if (socialImageUrl.startsWith("data:")) return toast.error("Necesitas una URL pública (no base64). Usa el botón de la biblioteca para obtener una URL servida.");
    if (!socialArt.wp_link) return toast.error("Publica primero el artículo en WordPress (se necesita la URL pública)");
    setSocialLoading(true);
    try {
      const r = await api.post(`/seo/articles/${socialArt.id}/post-social`, {
        image_url: socialImageUrl,
        custom_headline: socialHeadline || null,
        networks: ["facebook", "instagram"],
      });
      setSocialResult(r.data);
      if (r.data.errors?.length) {
        toast.warning(`Publicado con avisos: ${r.data.errors.join("; ")}`);
      } else {
        toast.success("Publicado en Facebook + Instagram");
      }
      loadAll(siteId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error publicando en redes");
    } finally {
      setSocialLoading(false);
    }
  };

  return (
    <div className="stagger">
      <PageHeader
        overline="Módulo SEO Pro"
        title="Studio editorial."
        subtitle="Plataforma completa de marketing de contenidos: Brand DNA, plan de keywords con datos reales, generación y publicación automática en WordPress."
        action={
          <div className="flex gap-2 items-end">
            <Select value={siteId} onValueChange={setSiteId}>
              <SelectTrigger className="rounded-sm bg-white border-[#E2DCD0] min-w-[280px]" data-testid="seo-site-select">
                <SelectValue placeholder="Selecciona un sitio..." />
              </SelectTrigger>
              <SelectContent>
                {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.url}</SelectItem>)}
              </SelectContent>
            </Select>
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button data-testid="seo-add-site" className="bg-[#0A1128] hover:bg-[#1c2547] text-white rounded-sm">
                  <Plus className="w-4 h-4 mr-1" />Añadir sitio
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white rounded-sm max-w-lg">
                <DialogHeader><DialogTitle className="font-display">Nuevo sitio</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>URL del sitio</Label><Input data-testid="site-url" value={siteForm.url} onChange={(e) => setSiteForm({ ...siteForm, url: e.target.value })} placeholder="https://tuagencia.com" className="mt-1 rounded-sm" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Usuario WordPress</Label><Input value={siteForm.wp_user} onChange={(e) => setSiteForm({ ...siteForm, wp_user: e.target.value })} className="mt-1 rounded-sm" /></div>
                    <div><Label>Application Password</Label><Input value={siteForm.wp_app_password} onChange={(e) => setSiteForm({ ...siteForm, wp_app_password: e.target.value })} placeholder="xxxx XXXX xxxx XXXX xxxx XXXX" className="mt-1 rounded-sm font-mono text-xs" /></div>
                  </div>
                  <p className="text-[11px] text-[#8A8F98]">Después del primer guardado, ve a Brand DNA para definir el contexto editorial del sitio.</p>
                </div>
                <DialogFooter><Button onClick={addSite} className="bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm">Guardar sitio</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      {!currentSite && (
        <Card className="edit-card p-12 rounded-sm shadow-none text-center text-[#5C616A]">
          Aún no tienes sitios. Añade el primero para comenzar.
        </Card>
      )}

      {currentSite && (
        <div className="grid lg:grid-cols-[200px_1fr] gap-6">
          {/* Sub-sidebar (Soro-style) */}
          <aside className="space-y-0.5">
            <Card className="edit-card p-3 rounded-sm shadow-none mb-3">
              <div className="flex items-center gap-2 mb-1">
                <Globe className="w-3.5 h-3.5 text-[#0A1128]" strokeWidth={1.5} />
                <p className="font-medium text-sm text-[#0A1128] truncate">{currentSite.url.replace(/^https?:\/\//, "")}</p>
              </div>
              {currentSite.wp_connected ? (
                <Badge variant="outline" className="rounded-sm text-[9px] border-[#2D6A4F] text-[#2D6A4F]"><Check className="w-2.5 h-2.5 mr-1" />WP conectado</Badge>
              ) : (
                <Badge variant="outline" className="rounded-sm text-[9px] border-[#D95D39] text-[#D95D39]"><AlertCircle className="w-2.5 h-2.5 mr-1" />WP no conectado</Badge>
              )}
              <div className="flex gap-1 mt-2">
                <button onClick={testSite} className="text-[10px] text-[#D95D39] hover:underline" data-testid="seo-wp-test">Probar</button>
                <span className="text-[#E2DCD0]">·</span>
                <button onClick={deleteSite} className="text-[10px] text-[#9D0208] hover:underline">Eliminar</button>
              </div>
            </Card>

            {SUBNAV.map((s) => (
              <button
                key={s.key}
                onClick={() => setSection(s.key)}
                data-testid={`seo-subnav-${s.key}`}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm text-left transition-colors ${section === s.key ? "bg-[#0A1128] text-white" : "text-[#1A1F2B] hover:bg-[#E2DCD0]/40"}`}
              >
                <s.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                <span className="flex-1">{s.label}</span>
                {s.badge && <span className="text-[9px] bg-[#D95D39]/20 text-[#D95D39] px-1.5 py-0.5 rounded-sm">{s.badge}</span>}
              </button>
            ))}
          </aside>

          <div className="min-w-0">
            {/* SiteGround warning */}
            {!currentSite.wp_connected && currentSite.wp_user_info?.error?.toString().includes("sgcaptcha") && (
              <div className="mb-5 p-3 bg-[#F9F8F6] border border-[#D95D39] rounded-sm text-xs text-[#5C616A]">
                <p className="font-medium text-[#9D0208] mb-1">SiteGround está bloqueando la conexión.</p>
                Tus credenciales son correctas. Para que el sistema pueda publicar, whitelist la IP <code className="bg-white px-1 border border-[#E2DCD0]">34.170.12.145</code> en SiteGround Site Tools → Security → Blacklist → Whitelist; o desactiva "AI Anti-Bot Challenge" para tu sitio.
              </div>
            )}

            {section === "home" && <HomeSection site={currentSite} keywords={keywords} articles={articles} onGo={setSection} />}

            {section === "brand" && (
              <BrandDNAEditor
                site={currentSite}
                onChange={updateBrandField}
                onSave={saveBrand}
              />
            )}

            {section === "keywords" && (
              <KeywordsSection
                site={currentSite}
                keywords={keywords}
                seedsForm={seedsForm}
                setSeedsForm={setSeedsForm}
                genLoading={genLoading}
                onGenerate={generatePlan}
                onRefreshPosition={refreshPosition}
                onArticleFromKw={genArticle}
              />
            )}

            {section === "articles" && (
              <ArticlesSection
                site={currentSite}
                articles={articles}
                planCount={planCount}
                setPlanCount={setPlanCount}
                planLoading={planLoading}
                onPlanGenerate={genPlanBatch}
                onEdit={setEditArt}
                onSchedule={(a) => { setScheduleArt(a); setScheduleDate(a.scheduled_at ? a.scheduled_at.slice(0, 16) : ""); }}
                onPublishWP={publishWP}
                onDelete={delArticle}
                onSocial={openSocialDialog}
                onAutoSocial={autoPostSocial}
                onRegenImage={regenerateImage}
                regenImageId={regenImageId}
              />
            )}

            {section === "calendar" && <CalendarView articles={articles} onClick={setEditArt} />}

            {section === "analytics" && <AnalyticsSection keywords={keywords} articles={articles} />}

            {section === "social" && (
              <SocialSection articles={articles} onSocial={openSocialDialog} onGenSocial={generateSocialCopy} genSocialId={genSocialId} />
            )}

            {section === "speed" && (
              <Card className="edit-card p-10 rounded-sm shadow-none text-center">
                <Gauge className="w-8 h-8 mx-auto text-[#8A8F98] mb-3" />
                <p className="font-display text-xl text-[#0A1128] mb-1">Site Speed</p>
                <p className="text-sm text-[#5C616A]">Próximamente: análisis Core Web Vitals + recomendaciones de optimización vía Google PageSpeed Insights.</p>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Edit article modal */}
      <Dialog open={!!editArt} onOpenChange={(o) => !o && setEditArt(null)}>
        <DialogContent className="bg-white rounded-sm max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">Editar artículo</DialogTitle></DialogHeader>
          {editArt && (
            <div className="space-y-3">
              <div><Label>Título</Label><Input value={editArt.title} onChange={(e) => setEditArt({ ...editArt, title: e.target.value })} className="mt-1 rounded-sm" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Slug</Label><Input value={editArt.slug} onChange={(e) => setEditArt({ ...editArt, slug: e.target.value })} className="mt-1 rounded-sm font-mono text-xs" /></div>
                <div><Label>Focus keyword</Label><Input value={editArt.focus_keyword || ""} onChange={(e) => setEditArt({ ...editArt, focus_keyword: e.target.value })} className="mt-1 rounded-sm" /></div>
              </div>
              <div><Label>Meta description</Label><Input value={editArt.meta_description} onChange={(e) => setEditArt({ ...editArt, meta_description: e.target.value })} maxLength={160} className="mt-1 rounded-sm" /></div>
              <div><Label>Extracto</Label><Textarea value={editArt.excerpt} onChange={(e) => setEditArt({ ...editArt, excerpt: e.target.value })} rows={2} className="mt-1 rounded-sm" /></div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Imagen destacada</Label>
                  <div className="flex gap-2">
                    <Select value={editArt._regen_style || "photorealistic"} onValueChange={(v) => setEditArt({ ...editArt, _regen_style: v })}>
                      <SelectTrigger className="h-8 w-40 text-xs rounded-sm"><SelectValue placeholder="Estilo" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="photorealistic">Fotorrealista</SelectItem>
                        <SelectItem value="clean">Limpio minimal</SelectItem>
                        <SelectItem value="watercolor">Acuarela</SelectItem>
                        <SelectItem value="3d">3D estilizado</SelectItem>
                        <SelectItem value="vintage">Vintage</SelectItem>
                        <SelectItem value="abstract">Abstracto</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      onClick={async () => {
                        toast.info("Generando imagen (~30s)...");
                        try {
                          const r = await api.post(`/seo/articles/${editArt.id}/regenerate-image`, {
                            style: editArt._regen_style || "photorealistic",
                          }, { timeout: 120000 });
                          setEditArt({ ...editArt, hero_image: r.data.hero_image, image_error: null });
                          loadAll(siteId);
                          toast.success("Imagen generada");
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Error generando imagen");
                        }
                      }}
                      className="bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm h-8"
                      data-testid="regen-image-btn"
                    >
                      <Sparkles className="w-3 h-3 mr-1" />{editArt.hero_image ? "Regenerar" : "Generar"}
                    </Button>
                  </div>
                </div>
                {editArt.hero_image ? (
                  <div className="border border-[#E2DCD0] rounded-sm overflow-hidden bg-[#F9F8F6]">
                    <img src={editArt.hero_image} alt="hero" className="w-full max-h-80 object-cover" />
                  </div>
                ) : (
                  <div className="border border-dashed border-[#E2DCD0] rounded-sm p-6 text-center text-xs text-[#8A8F98] bg-[#F9F8F6]">
                    Sin imagen destacada. Click en "Generar" para crear una con IA.
                  </div>
                )}
                {editArt.image_error && (
                  <div className="text-xs text-[#9D0208] bg-[#9D0208]/5 border border-[#9D0208]/30 p-2 rounded-sm">
                    Último error: {editArt.image_error}
                  </div>
                )}
              </div>

              <div><Label>Contenido (HTML)</Label><Textarea value={editArt.content} onChange={(e) => setEditArt({ ...editArt, content: e.target.value })} rows={20} className="mt-1 rounded-sm font-mono text-xs" /></div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setEditArt(null)} variant="outline" className="rounded-sm">Cancelar</Button>
            <Button onClick={saveEdit} className="bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Schedule modal */}
      <Dialog open={!!scheduleArt} onOpenChange={(o) => !o && setScheduleArt(null)}>
        <DialogContent className="bg-white rounded-sm max-w-md">
          <DialogHeader><DialogTitle className="font-display">Programar publicación</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Fecha y hora</Label>
            <Input type="datetime-local" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} className="rounded-sm" />
          </div>
          <DialogFooter>
            <Button onClick={() => setScheduleArt(null)} variant="outline" className="rounded-sm">Cancelar</Button>
            <Button onClick={scheduleArticle} className="bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm">Programar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Social posting — Asistente de Publicación */}
      <Dialog open={!!socialArt} onOpenChange={(o) => !o && setSocialArt(null)}>
        <DialogContent className="bg-white rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Share2 className="w-4 h-4 text-[#D95D39]" />
              Asistente de publicación · Facebook + Instagram
            </DialogTitle>
          </DialogHeader>
          {socialArt && (
            <div className="space-y-4">
              <div className="p-3 bg-[#F9F8F6] border border-[#E2DCD0] rounded-sm">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] mb-1">Artículo</p>
                <p className="font-medium text-[#0A1128] text-sm">{socialArt.title}</p>
                {socialArt.wp_link && (
                  <a href={socialArt.wp_link} target="_blank" rel="noreferrer" className="text-xs text-[#D95D39] mt-1 break-all font-mono inline-block">
                    {socialArt.wp_link} <ExternalLink className="w-3 h-3 inline" />
                  </a>
                )}
              </div>

              {/* Imagen */}
              {socialArt.hero_image && (
                <div className="border border-[#E2DCD0] rounded-sm overflow-hidden bg-[#F9F8F6]">
                  <img src={socialArt.hero_image} alt={socialArt.image_alt || socialArt.title} className="w-full max-h-64 object-cover" />
                  <div className="p-3 flex items-center justify-between bg-white border-t border-[#E2DCD0]">
                    <span className="text-xs text-[#5C616A]">Imagen generada con IA</span>
                    <a
                      href={socialArt.hero_image}
                      download={`${socialArt.slug || "image"}.png`}
                      data-testid="download-image-btn"
                    >
                      <Button size="sm" variant="outline" className="rounded-sm border-[#E2DCD0]">
                        <Download className="w-3 h-3 mr-1" />Descargar imagen
                      </Button>
                    </a>
                  </div>
                </div>
              )}

              {/* Copy Facebook */}
              <div className="border border-[#1877F2]/30 rounded-sm p-4 bg-[#1877F2]/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Facebook className="w-4 h-4 text-[#1877F2]" />
                    <span className="font-display text-sm text-[#0A1128]">Post para Facebook</span>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      const txt = (socialArt.fb_caption || "") + (socialArt.wp_link ? `\n\n${socialArt.wp_link}` : "");
                      navigator.clipboard.writeText(txt);
                      toast.success("Copiado al portapapeles");
                    }}
                    className="bg-[#1877F2] hover:bg-[#0d5fbd] text-white rounded-sm h-7"
                    data-testid="copy-fb-btn"
                  >
                    <Copy className="w-3 h-3 mr-1" />Copiar
                  </Button>
                </div>
                <Textarea
                  rows={4}
                  value={(socialArt.fb_caption || "") + (socialArt.wp_link ? `\n\n${socialArt.wp_link}` : "")}
                  readOnly
                  className="w-full text-sm rounded-sm bg-white border-[#E2DCD0]"
                />
                <a href="https://www.facebook.com/" target="_blank" rel="noreferrer" className="inline-block mt-2">
                  <Button size="sm" variant="outline" className="rounded-sm border-[#1877F2] text-[#1877F2] hover:bg-[#1877F2]/10">
                    <ExternalLink className="w-3 h-3 mr-1" />Abrir Facebook
                  </Button>
                </a>
              </div>

              {/* Copy Instagram */}
              <div className="border border-[#E4405F]/30 rounded-sm p-4 bg-[#E4405F]/5">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Instagram className="w-4 h-4 text-[#E4405F]" />
                    <span className="font-display text-sm text-[#0A1128]">Post para Instagram</span>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      const txt = `${socialArt.ig_caption || ""}\n\n— Link en bio —\n\n${socialArt.ig_hashtags || ""}`.trim();
                      navigator.clipboard.writeText(txt);
                      toast.success("Copiado al portapapeles");
                    }}
                    className="bg-[#E4405F] hover:bg-[#c4324c] text-white rounded-sm h-7"
                    data-testid="copy-ig-btn"
                  >
                    <Copy className="w-3 h-3 mr-1" />Copiar todo
                  </Button>
                </div>
                <Textarea
                  rows={5}
                  value={`${socialArt.ig_caption || ""}\n\n— Link en bio —\n\n${socialArt.ig_hashtags || ""}`.trim()}
                  readOnly
                  className="w-full text-sm rounded-sm bg-white border-[#E2DCD0]"
                />
                <a href="https://www.instagram.com/" target="_blank" rel="noreferrer" className="inline-block mt-2">
                  <Button size="sm" variant="outline" className="rounded-sm border-[#E4405F] text-[#E4405F] hover:bg-[#E4405F]/10">
                    <ExternalLink className="w-3 h-3 mr-1" />Abrir Instagram
                  </Button>
                </a>
              </div>

              <div className="p-3 bg-[#F9F8F6] border border-[#E2DCD0] rounded-sm text-xs text-[#5C616A]">
                <strong className="text-[#0A1128]">Cómo publicar (30 segundos por red):</strong>
                <ol className="list-decimal ml-4 mt-1 space-y-1">
                  <li>Descarga la imagen (botón arriba)</li>
                  <li>Pulsa "Copiar" en Facebook → abre Facebook → crear publicación → pega → adjunta la imagen → publicar</li>
                  <li>Pulsa "Copiar todo" en Instagram → abre Instagram (móvil) → crear post → adjunta imagen → pega caption → publicar</li>
                  <li>Vuelve aquí y marca el artículo como publicado en RRSS si quieres llevar control</li>
                </ol>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setSocialArt(null)} variant="outline" className="rounded-sm">Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================ SUB-SECTIONS ============================ */

function HomeSection({ site, keywords, articles, onGo }) {
  const pub = articles.filter((a) => a.status === "published").length;
  const sched = articles.filter((a) => a.status === "scheduled").length;
  const draft = articles.filter((a) => a.status === "draft").length;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile label="Keywords" value={keywords.length} icon={SearchIcon} onClick={() => onGo("keywords")} />
        <Tile label="Publicados" value={pub} icon={FileText} onClick={() => onGo("articles")} />
        <Tile label="Programados" value={sched} icon={CalIcon} onClick={() => onGo("calendar")} />
        <Tile label="Borradores" value={draft} icon={FileText} onClick={() => onGo("articles")} />
      </div>
      <Card className="edit-card p-6 rounded-sm shadow-none">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98]">Empieza por aquí</p>
        <ol className="mt-4 space-y-3 text-sm text-[#1A1F2B]">
          <li><strong>1.</strong> Completa el <button onClick={() => onGo("brand")} className="text-[#D95D39] underline">Brand DNA</button> con tu identidad, audiencia, destinos y temas clave.</li>
          <li><strong>2.</strong> Genera el plan de <button onClick={() => onGo("keywords")} className="text-[#D95D39] underline">Keywords</button> con datos reales de DataForSEO.</li>
          <li><strong>3.</strong> Crea <button onClick={() => onGo("articles")} className="text-[#D95D39] underline">Artículos</button> uno a uno o usa "Generar plan" para X artículos programados.</li>
          <li><strong>4.</strong> Revisa el <button onClick={() => onGo("calendar")} className="text-[#D95D39] underline">Calendario</button> editorial — el scheduler publica solo cada 5 minutos.</li>
        </ol>
      </Card>

      <Card className="edit-card p-6 rounded-sm shadow-none border-l-4 border-l-[#D95D39]">
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#8A8F98]">Plugin de WordPress</p>
        <p className="font-display text-lg text-[#0A1128] mt-2">Activa el SEO completo + el chat Maya en tu WordPress</p>
        <p className="text-sm text-[#5C616A] mt-2">
          Yoast Free <strong>no expone</strong> sus campos SEO en la API. Sin este plugin, los meta titles, meta descriptions, Open Graph y Twitter Cards que generamos se ignoran al publicar. Este plugin además inyecta el widget de chat Maya en toda tu web en 1 clic.
        </p>
        <div className="flex flex-wrap gap-3 mt-4">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/static/espiritu-travel-helper.zip`}
            download
            data-testid="download-wp-plugin"
          >
            <Button className="bg-[#0A1128] hover:bg-[#1B2247] text-white rounded-sm">
              Descargar plugin (.zip)
            </Button>
          </a>
          <span className="text-xs text-[#8A8F98] self-center">
            Súbelo en WP Admin → Plugins → Añadir nuevo → Subir plugin → Activar.
          </span>
        </div>
      </Card>
    </div>
  );
}

function Tile({ label, value, icon: Icon, onClick }) {
  return (
    <button onClick={onClick} className="text-left">
      <Card className="edit-card p-5 rounded-sm shadow-none h-full">
        <div className="flex items-center justify-between mb-3">
          <Icon className="w-4 h-4 text-[#8A8F98]" strokeWidth={1.5} />
          <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98]">{label}</span>
        </div>
        <div className="font-mono text-3xl text-[#0A1128]">{value}</div>
      </Card>
    </button>
  );
}

function ChipList({ items, onChange, placeholder, testid }) {
  const [draft, setDraft] = useState("");
  const arr = Array.isArray(items) ? items : [];
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        {arr.map((it, i) => (
          <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-[#0A1128]/5 border border-[#E2DCD0] rounded-sm text-xs text-[#0A1128]">
            {it}
            <button onClick={() => onChange(arr.filter((_, idx) => idx !== i))} type="button"><X className="w-3 h-3 text-[#8A8F98] hover:text-[#9D0208]" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          data-testid={testid}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && draft.trim()) {
              e.preventDefault();
              onChange([...arr, draft.trim()]);
              setDraft("");
            }
          }}
          placeholder={placeholder}
          className="rounded-sm bg-white border-[#E2DCD0] text-sm"
        />
        <Button type="button" size="sm" variant="outline" onClick={() => { if (draft.trim()) { onChange([...arr, draft.trim()]); setDraft(""); } }} className="rounded-sm border-[#E2DCD0]">+ Añadir</Button>
      </div>
    </div>
  );
}

function BrandDNAEditor({ site, onChange, onSave }) {
  return (
    <div className="space-y-4">
      <Card className="edit-card p-6 rounded-sm shadow-none">
        <div className="flex items-start justify-between mb-1">
          <h2 className="font-display text-3xl text-[#0A1128]">Brand DNA</h2>
          <Button onClick={onSave} className="bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm" data-testid="brand-save">Guardar Brand DNA</Button>
        </div>
        <p className="text-sm text-[#5C616A] mb-6">La IA usa este perfil para escribir keywords y artículos alineados con tu marca. Ajústalo cuando quieras.</p>

        <BrandField label="Content Language" hint="Idioma del contenido generado">
          <Select value={site.content_language || "es"} onValueChange={(v) => onChange("content_language", v)}>
            <SelectTrigger className="mt-1 rounded-sm bg-white border-[#E2DCD0] max-w-xs" data-testid="brand-lang"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="es">Español</SelectItem>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="fr">Français</SelectItem>
              <SelectItem value="it">Italiano</SelectItem>
              <SelectItem value="pt">Português</SelectItem>
            </SelectContent>
          </Select>
        </BrandField>

        <BrandField label="Business Type" hint="Describe el tipo de negocio, modelo, propuesta de valor">
          <Textarea data-testid="brand-business-type" rows={6} value={site.business_type || ""} onChange={(e) => onChange("business_type", e.target.value)} className="mt-1 rounded-sm" />
        </BrandField>

        <BrandField label="Brand Voice & Tone" hint="Cómo habla la marca: tono, personalidad">
          <Textarea data-testid="brand-voice" rows={4} value={site.brand_voice || ""} onChange={(e) => onChange("brand_voice", e.target.value)} className="mt-1 rounded-sm" />
        </BrandField>

        <BrandField label="Target Audience" hint="Perfil completo de tu cliente ideal">
          <Textarea data-testid="brand-audience" rows={5} value={site.target_audience || ""} onChange={(e) => onChange("target_audience", e.target.value)} className="mt-1 rounded-sm" />
        </BrandField>

        <BrandField label="Service Area" hint="Países/regiones donde ofreces tus servicios">
          <ChipList items={site.service_area} onChange={(v) => onChange("service_area", v)} placeholder="Ej: Spain (Enter para añadir)" testid="brand-service-area" />
        </BrandField>

        <BrandField label="Key Themes" hint="Temas centrales en torno a los que pivota tu contenido">
          <ChipList items={site.key_themes} onChange={(v) => onChange("key_themes", v)} placeholder="Ej: Authentic Italy itineraries" testid="brand-key-themes" />
        </BrandField>

        <BrandField label="Topics to Avoid" hint="Ej: nombres de competidores, servicios que no ofreces" optional>
          <ChipList items={site.topics_to_avoid} onChange={(v) => onChange("topics_to_avoid", v)} placeholder="Tema a evitar" testid="brand-topics-avoid" />
        </BrandField>

        <BrandField label="Business Goals" hint="Qué quieres conseguir con el contenido" optional>
          <Textarea rows={3} value={site.business_goals || ""} onChange={(e) => onChange("business_goals", e.target.value)} className="mt-1 rounded-sm" />
        </BrandField>

        <BrandField label="Differentiators" hint="Qué te hace diferente de la competencia" optional>
          <Textarea rows={3} value={site.differentiators || ""} onChange={(e) => onChange("differentiators", e.target.value)} className="mt-1 rounded-sm" />
        </BrandField>
      </Card>

      {/* Image generation */}
      <Card className="edit-card p-6 rounded-sm shadow-none">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="font-medium text-[#0A1128]">Create Images For My Articles</p>
            <p className="text-xs text-[#5C616A]">VoyageMarkt genera imágenes para tus artículos con IA</p>
          </div>
          <Switch
            checked={site.create_images !== false}
            onCheckedChange={(v) => onChange("create_images", v)}
            data-testid="brand-create-images"
          />
        </div>

        {site.create_images !== false && (
          <>
            <BrandField label="Custom Image Instructions" hint="Prompt base que la IA usará para todas las imágenes" optional>
              <Textarea
                data-testid="brand-image-instructions"
                rows={5}
                value={site.custom_image_instructions || ""}
                onChange={(e) => onChange("custom_image_instructions", e.target.value)}
                placeholder="Ej: Create an aspirational, photorealistic travel image for X. Show... Golden hour, premium editorial style..."
                className="mt-1 rounded-sm"
              />
            </BrandField>

            <p className="text-xs uppercase tracking-wider text-[#5C616A] mb-3">Image Style</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {IMAGE_STYLES.map((st) => {
                const selected = (site.image_style || "photorealistic") === st.id;
                return (
                  <button
                    key={st.id}
                    type="button"
                    onClick={() => onChange("image_style", st.id)}
                    data-testid={`brand-style-${st.id}`}
                    className={`text-left p-4 rounded-sm border transition-colors ${selected ? "border-[#D95D39] ring-2 ring-[#D95D39]" : "border-[#E2DCD0] hover:border-[#D95D39]"}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium text-sm text-[#0A1128]">{st.label}</p>
                      {selected && <Check className="w-4 h-4 text-[#D95D39]" />}
                    </div>
                    <p className="text-xs text-[#5C616A]">{st.desc}</p>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function BrandField({ label, hint, optional, children }) {
  return (
    <div className="py-4 border-t border-[#E2DCD0] first:border-t-0 first:pt-0">
      <div className="flex items-center gap-2 mb-1">
        <p className="font-medium text-[#0A1128]">{label}</p>
        {optional && <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] bg-[#F9F8F6] px-2 py-0.5 rounded-sm border border-[#E2DCD0]">Optional</span>}
      </div>
      {hint && <p className="text-xs text-[#5C616A] mb-2">{hint}</p>}
      {children}
    </div>
  );
}

function KeywordsSection({ site, keywords, seedsForm, setSeedsForm, genLoading, onGenerate, onRefreshPosition, onArticleFromKw }) {
  return (
    <div className="space-y-5">
      <Card className="edit-card p-6 rounded-sm shadow-none">
        <h3 className="font-display text-2xl text-[#0A1128] mb-1">Plan de keywords</h3>
        <p className="text-xs text-[#8A8F98] mb-4">
          La IA usa tu Brand DNA + DataForSEO para producir keywords con volúmenes reales, intención, cluster y prioridad.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>Semillas extra (opcional)</Label>
            <Input value={seedsForm.seeds} onChange={(e) => setSeedsForm({ ...seedsForm, seeds: e.target.value })} placeholder="cyprus, marrakech, douro valley" className="mt-1 rounded-sm" />
            <p className="text-[11px] text-[#8A8F98] mt-1">Sin semillas, usa el Service Area de tu Brand DNA.</p>
          </div>
          <div>
            <Label>Nicho adicional</Label>
            <Input value={seedsForm.audience} onChange={(e) => setSeedsForm({ ...seedsForm, audience: e.target.value })} placeholder="Honeymoons" className="mt-1 rounded-sm" />
          </div>
        </div>
        <Button data-testid="seo-generate-keywords" onClick={onGenerate} disabled={genLoading} className="mt-4 bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm">
          <Sparkles className="w-4 h-4 mr-2" />{genLoading ? "Analizando..." : "Generar plan de keywords"}
        </Button>
      </Card>

      {keywords.length > 0 && (
        <Card className="edit-card p-0 rounded-sm shadow-none overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="border-b border-[#E2DCD0] text-left">
                <th className="py-3 px-4 font-medium text-[#5C616A]">Keyword</th>
                <th className="py-3 px-4 font-medium text-[#5C616A]">Cluster</th>
                <th className="py-3 px-4 font-medium text-[#5C616A]">Intent</th>
                <th className="py-3 px-4 font-medium text-[#5C616A]">Vol.</th>
                <th className="py-3 px-4 font-medium text-[#5C616A]">CPC</th>
                <th className="py-3 px-4 font-medium text-[#5C616A]">Comp.</th>
                <th className="py-3 px-4 font-medium text-[#5C616A]">Prio.</th>
                <th className="py-3 px-4 font-medium text-[#5C616A]">Pos.</th>
                <th className="py-3 px-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2DCD0]">
              {keywords.map((k) => (
                <tr key={k.id} className="hover:bg-[#F9F8F6]">
                  <td className="py-3 px-4 font-mono text-[#0A1128] max-w-[240px] truncate">{k.keyword}</td>
                  <td className="py-3 px-4 text-[#5C616A] text-xs">{k.cluster || "—"}</td>
                  <td className="py-3 px-4 text-[#5C616A] text-xs">{k.intent || "—"}</td>
                  <td className="py-3 px-4 font-mono">{k.search_volume?.toLocaleString() || "—"}</td>
                  <td className="py-3 px-4 font-mono">{k.cpc ? `€${k.cpc}` : "—"}</td>
                  <td className="py-3 px-4 text-xs">{k.competition || "—"}</td>
                  <td className="py-3 px-4"><Badge variant="outline" className={`rounded-sm text-[10px] ${PRIORITY_STYLES[k.priority] || ""}`}>{k.priority || "—"}</Badge></td>
                  <td className="py-3 px-4 font-mono text-[#D95D39]">{k.current_position ? `#${k.current_position}` : "—"}</td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex gap-1 justify-end">
                      <Button size="icon" variant="outline" onClick={() => onRefreshPosition(k)} title="Refrescar posición" className="h-7 w-7 rounded-sm border-[#E2DCD0]">
                        <TrendingUp className="w-3 h-3" />
                      </Button>
                      <Button size="sm" onClick={() => onArticleFromKw(k)} className="h-7 bg-[#0A1128] hover:bg-[#1c2547] text-white rounded-sm text-xs">
                        Crear artículo
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function SocialSection({ articles, onSocial, onGenSocial, genSocialId }) {
  // Show ALL articles — assistant works without WP publish
  const sorted = [...articles].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  return (
    <div className="space-y-4">
      <Card className="edit-card p-6 rounded-sm shadow-none">
        <div className="flex items-center gap-2 mb-3">
          <Facebook className="w-5 h-5 text-[#1877F2]" />
          <Instagram className="w-5 h-5 text-[#E4405F]" />
          <h3 className="font-display text-2xl text-[#0A1128]">Redes sociales — Asistente</h3>
        </div>
        <p className="text-sm text-[#5C616A] mb-2">
          La IA genera el copy de Facebook + Instagram + hashtags y la imagen lista para cada artículo. Tú haces clic en "Asistente RRSS", copias el texto, descargas la imagen y publicas en 30 segundos.
        </p>
        <p className="text-xs text-[#8A8F98]">
          Sin tokens, sin caducidad, sin Meta App Review. Funciona desde el primer día.
        </p>
      </Card>

      {sorted.length === 0 && (
        <Card className="edit-card p-10 rounded-sm shadow-none text-center text-[#5C616A]">
          Aún no hay artículos. Crea alguno desde Keywords o Artículos.
        </Card>
      )}

      {sorted.map((a) => {
        const hasCopy = !!(a.fb_caption || a.ig_caption);
        return (
          <Card key={a.id} className="edit-card p-5 rounded-sm shadow-none flex flex-col md:flex-row md:items-center gap-3 justify-between">
            <div className="min-w-0 flex items-center gap-3 flex-1">
              {a.hero_image ? (
                <img src={a.hero_image} alt={a.title} className="w-16 h-16 object-cover rounded-sm shrink-0 border border-[#E2DCD0]" />
              ) : (
                <div className="w-16 h-16 shrink-0 bg-[#F2EFE6] border border-[#E2DCD0] rounded-sm flex items-center justify-center">
                  <ImageIcon className="w-5 h-5 text-[#8A8F98]" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <h4 className="font-display text-lg text-[#0A1128] truncate">{a.title}</h4>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  {hasCopy ? (
                    <Badge variant="outline" className="rounded-sm text-[10px] border-[#2D6A4F] text-[#2D6A4F]"><Check className="w-2 h-2 mr-1" />Copy listo</Badge>
                  ) : (
                    <Badge variant="outline" className="rounded-sm text-[10px] border-[#D95D39] text-[#D95D39]">Sin copy de RRSS</Badge>
                  )}
                  {a.wp_link && <a href={a.wp_link} target="_blank" rel="noreferrer" className="text-xs text-[#5C616A] font-mono truncate hover:text-[#D95D39]">{a.wp_link}</a>}
                </div>
                {a.social_log?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {a.social_log.map((s, i) => (
                      <Badge key={i} variant="outline" className="rounded-sm text-[9px] border-[#2D6A4F] text-[#2D6A4F]"><Check className="w-2 h-2 mr-1" />Posteado {new Date(s.posted_at).toLocaleDateString("es")}</Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              {!hasCopy && (
                <Button
                  onClick={() => onGenSocial(a)}
                  disabled={genSocialId === a.id}
                  variant="outline"
                  className="rounded-sm border-[#D95D39] text-[#D95D39] hover:bg-[#D95D39]/10"
                  data-testid={`gen-social-copy-${a.id}`}
                >
                  {genSocialId === a.id ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Generando</> : <><Sparkles className="w-3 h-3 mr-1" />Generar copy IA</>}
                </Button>
              )}
              <Button
                onClick={() => onSocial(a)}
                disabled={!hasCopy}
                className="bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm disabled:opacity-40"
                data-testid={`social-section-open-${a.id}`}
              >
                <Share2 className="w-3 h-3 mr-1" />Asistente RRSS
              </Button>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function ArticlesSection({ site, articles, planCount, setPlanCount, planLoading, onPlanGenerate, onEdit, onSchedule, onPublishWP, onDelete, onSocial, onAutoSocial, onRegenImage, regenImageId }) {
  const sorted = [...articles].sort((a, b) => {
    const da = a.scheduled_at || a.published_at || a.created_at;
    const dbb = b.scheduled_at || b.published_at || b.created_at;
    return dbb.localeCompare(da);
  });
  return (
    <div className="space-y-4">
      <Card className="edit-card p-5 rounded-sm shadow-none flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-xl text-[#0A1128]">Plan editorial automático</h3>
          <p className="text-xs text-[#5C616A]">Genera N artículos en borrador, programados en los días de publicación configurados (días {(site.posting_days || []).join(", ")}, {site.posting_hour || 9}:00).</p>
        </div>
        <div className="flex items-center gap-2">
          <Input type="number" min="1" max="20" value={planCount} onChange={(e) => setPlanCount(e.target.value)} className="w-20 rounded-sm border-[#E2DCD0]" />
          <Button onClick={onPlanGenerate} disabled={planLoading} className="bg-[#D95D39] text-white rounded-sm" data-testid="seo-plan-generate">
            <Sparkles className="w-4 h-4 mr-1" />Generar plan
          </Button>
        </div>
      </Card>

      {sorted.length === 0 && (
        <Card className="edit-card p-10 rounded-sm shadow-none text-center text-[#5C616A]">
          Aún no hay artículos. Crea el primero desde Keywords o usa "Generar plan".
        </Card>
      )}
      {sorted.map((a) => (
        <Card key={a.id} className="edit-card p-5 rounded-sm shadow-none">
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            <div className="w-full sm:w-40 h-32 sm:h-28 shrink-0 rounded-sm overflow-hidden border border-[#E2DCD0] relative bg-[#F2EFE6] flex items-center justify-center">
              {a.hero_image ? (
                <img src={a.hero_image} alt={a.image_alt || a.title} className="w-full h-full object-cover" />
              ) : (
                <div className="text-center p-2">
                  <ImageIcon className="w-7 h-7 mx-auto text-[#8A8F98] mb-1" strokeWidth={1.5} />
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98]">Sin imagen</p>
                </div>
              )}
              <Button
                size="sm"
                onClick={() => onRegenImage(a)}
                disabled={regenImageId === a.id}
                className="absolute bottom-1 right-1 h-7 px-2 text-[10px] bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm shadow-md"
                data-testid={`article-regen-image-${a.id}`}
              >
                {regenImageId === a.id ? (
                  <><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Generando</>
                ) : a.hero_image ? (
                  <><Wand2 className="w-3 h-3 mr-1" />Regenerar</>
                ) : (
                  <><Sparkles className="w-3 h-3 mr-1" />Generar</>
                )}
              </Button>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Badge variant="outline" className={`rounded-sm text-[10px] ${STATUS_STYLES[a.status]}`}>{a.status}</Badge>
                {a.focus_keyword && <Badge variant="outline" className="rounded-sm text-[10px]">{a.focus_keyword}</Badge>}
                {a.scheduled_at && <Badge variant="outline" className="rounded-sm text-[10px] border-[#D95D39] text-[#D95D39]"><CalIcon className="w-3 h-3 mr-1" />{new Date(a.scheduled_at).toLocaleString("es")}</Badge>}
              </div>
              <h4 className="font-display text-xl text-[#0A1128] leading-tight break-words">{a.title}</h4>
              <p className="text-sm text-[#5C616A] mt-1.5 leading-relaxed break-words line-clamp-2">{a.meta_description}</p>
              <p className="text-[11px] text-[#8A8F98] mt-2 font-mono truncate">/{a.slug}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-[#F0EBE2]">
            <Button size="sm" variant="outline" onClick={() => onEdit(a)} className="rounded-sm border-[#E2DCD0]">Editar</Button>
            <Button size="sm" variant="outline" onClick={() => onSchedule(a)} className="rounded-sm border-[#E2DCD0]">Programar</Button>
            <Button size="sm" onClick={() => onPublishWP(a, "draft")} variant="outline" className="rounded-sm border-[#E2DCD0]">WP: Borrador</Button>
            <Button size="sm" onClick={() => onPublishWP(a, "publish")} className="bg-[#2D6A4F] hover:bg-[#1B4332] text-white rounded-sm">WP: Publicar ya</Button>
            <Button size="sm" onClick={() => onSocial(a)} className="bg-[#D95D39] hover:bg-[#c54e2e] text-white rounded-sm" data-testid={`article-social-${a.id}`} title="Asistente con el copy de FB + IG + imagen lista para copiar y publicar">
              <Share2 className="w-3 h-3 mr-1" />Asistente RRSS
            </Button>
            {a.wp_link && (
              <a href={a.wp_link} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" className="rounded-sm border-[#E2DCD0]"><ExternalLink className="w-3 h-3 mr-1" />Ver</Button>
              </a>
            )}
            <div className="ml-auto">
              <Button size="icon" variant="outline" onClick={() => onDelete(a.id)} className="h-9 w-9 rounded-sm border-[#E2DCD0] hover:border-[#9D0208] hover:text-[#9D0208]">
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
          {a.wp_error && <p className="mt-3 text-xs text-[#9D0208]">Error WP: {a.wp_error}</p>}
        </Card>
      ))}
    </div>
  );
}

function CalendarView({ articles, onClick }) {
  const today = new Date();
  const start = new Date(today);
  const day = (start.getDay() + 6) % 7;
  start.setDate(start.getDate() - day);
  start.setHours(0, 0, 0, 0);
  const days = [];
  for (let i = 0; i < 35; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  const byDay = (d) => articles.filter((a) => {
    const at = a.scheduled_at || a.published_at;
    if (!at) return false;
    return new Date(at).toDateString() === d.toDateString();
  });
  return (
    <div>
      <div className="grid grid-cols-7 gap-2 mb-2">
        {["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"].map((d) => (
          <div key={d} className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {days.map((d, i) => {
          const items = byDay(d);
          const isToday = d.toDateString() === today.toDateString();
          return (
            <Card key={i} className={`edit-card p-2 rounded-sm shadow-none min-h-[110px] ${isToday ? "border-[#D95D39] border-2" : ""}`}>
              <p className="text-[10px] text-[#8A8F98] mb-1 font-mono">{d.getDate()}/{d.getMonth() + 1}</p>
              <div className="space-y-1">
                {items.map((a) => (
                  <button key={a.id} onClick={() => onClick(a)} className={`block text-left w-full text-[10px] p-1 rounded-sm ${a.status === "published" ? "bg-[#2D6A4F]/10 text-[#1B4332]" : "bg-[#D95D39]/10 text-[#9b3f1f]"} truncate hover:opacity-80`}>
                    <span className="font-medium">{new Date(a.scheduled_at || a.published_at).toLocaleTimeString("es", { hour: "2-digit", minute: "2-digit" })}</span>{" "}{a.title}
                  </button>
                ))}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function AnalyticsSection({ keywords, articles }) {
  const ranking = keywords.filter((k) => k.current_position).sort((a, b) => a.current_position - b.current_position);
  const totalVol = keywords.reduce((s, k) => s + (k.search_volume || 0), 0);
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Tile label="Volumen total mensual" value={totalVol.toLocaleString()} icon={TrendingUp} />
        <Tile label="Keywords con posición" value={ranking.length} icon={SearchIcon} />
        <Tile label="Artículos publicados" value={articles.filter((a) => a.status === "published").length} icon={FileText} />
        <Tile label="Artículos programados" value={articles.filter((a) => a.status === "scheduled").length} icon={CalIcon} />
      </div>

      <Card className="edit-card p-6 rounded-sm shadow-none">
        <h3 className="font-display text-2xl text-[#0A1128] mb-4">Top posiciones</h3>
        {ranking.length === 0 ? (
          <p className="text-sm text-[#5C616A]">Aún no has refrescado posiciones. Ve a Keywords → botón <TrendingUp className="inline w-3 h-3" /> en cada keyword.</p>
        ) : (
          <ul className="divide-y divide-[#E2DCD0]">
            {ranking.slice(0, 10).map((k) => (
              <li key={k.id} className="py-3 flex justify-between items-center">
                <span className="font-mono text-sm text-[#0A1128] truncate">{k.keyword}</span>
                <Badge variant="outline" className="rounded-sm border-[#D95D39] text-[#D95D39]">#{k.current_position}</Badge>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
